import React, { useState, useEffect } from 'react';
import { Outlet, useSearchParams } from 'react-router-dom';
import { Sidebar } from './ui/Sidebar';
import { TrialBanner } from './ui/TrialBanner';
import { useSubscription } from '../hooks/useSubscription';

export const DashboardLayout: React.FC = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const { refresh } = useSubscription();
  const [searchParams, setSearchParams] = useSearchParams();

  // Refresh subscription data after returning from Stripe Checkout
  useEffect(() => {
    const billing = searchParams.get('billing');
    if (billing === 'success') {
      refresh();
      searchParams.delete('billing');
      setSearchParams(searchParams, { replace: true });
    } else if (billing === 'canceled') {
      searchParams.delete('billing');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, refresh]);

  // Close the mobile drawer when crossing to desktop, so it isn't stale-open
  // when returning to a mobile viewport (mobile-ui.5.B)
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = (e: MediaQueryListEvent) => { if (e.matches) setIsMobileOpen(false); };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return (
    <div className="min-h-dvh bg-neutral-950">
      {/* Background Blobs */}
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-[-10%] right-[-10%] w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[100px] mix-blend-screen"
          style={{ animation: 'floatBlob 15s ease-in-out infinite' }}
        />
        <div
          className="absolute bottom-[-20%] left-[-5%] w-[400px] h-[400px] bg-purple-600/10 rounded-full blur-[80px] mix-blend-screen"
          style={{ animation: 'floatBlob 20s ease-in-out infinite reverse' }}
        />
      </div>

      {/* Sidebar */}
      <Sidebar
        isCollapsed={isCollapsed}
        onToggle={() => {
          if (window.innerWidth < 1024) {
            setIsMobileOpen(!isMobileOpen);
          } else {
            setIsCollapsed(!isCollapsed);
          }
        }}
        isMobileOpen={isMobileOpen}
        onMobileClose={() => setIsMobileOpen(false)}
      />

      {/* Main Content */}
      <main
        className={`
          relative min-h-dvh
          transition-all duration-300
          ${isCollapsed ? 'lg:ml-20' : 'lg:ml-64'}
        `}
      >
        <div className="p-4 pt-20 lg:pt-8 lg:p-8">
          <TrialBanner />
          <Outlet />
        </div>
      </main>
    </div>
  );
};
